import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
const FN_URL = `${SUPABASE_URL}/functions/v1/handle-email-unsubscribe`;

type State =
  | "validating"
  | "ready"
  | "already"
  | "invalid"
  | "submitting"
  | "done"
  | "error";

const Unsubscribe = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState<State>("validating");

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    (async () => {
      try {
        const res = await fetch(`${FN_URL}?token=${encodeURIComponent(token)}`, {
          headers: { apikey: SUPABASE_ANON_KEY },
        });
        const data = await res.json();
        if (res.ok && data.valid) setState("ready");
        else if (data?.reason === "already_unsubscribed") setState("already");
        else setState("invalid");
      } catch { setState("error"); }
    })();
  }, [token]);

  const confirm = async () => {
    if (!token) return;
    setState("submitting");
    try {
      const res = await fetch(FN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: SUPABASE_ANON_KEY },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (res.ok && (data.success || data.reason === "already_unsubscribed")) setState("done");
      else setState("error");
    } catch { setState("error"); }
  };

  return (
    <div className="pt-32 pb-20 min-h-[60vh] flex items-center justify-center">
      <div className="max-w-md mx-auto text-center px-4">
        {state === "validating" && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <p className="font-display tracking-widest">CHECKING LINK…</p>
          </>
        )}
        {state === "ready" && (
          <>
            <Mail className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-display tracking-widest mb-3">UNSUBSCRIBE?</h1>
            <p className="text-muted-foreground font-body mb-8">
              You'll stop receiving non-essential emails from MyStudio. You'll still receive
              critical messages tied to active bookings.
            </p>
            <Button
              onClick={confirm}
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-display tracking-widest"
            >
              CONFIRM UNSUBSCRIBE
            </Button>
          </>
        )}
        {state === "submitting" && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <p className="font-display tracking-widest">PROCESSING…</p>
          </>
        )}
        {(state === "done" || state === "already") && (
          <>
            <CheckCircle2 className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-display tracking-widest mb-3">
              {state === "already" ? "ALREADY UNSUBSCRIBED" : "UNSUBSCRIBED"}
            </h1>
            <p className="text-muted-foreground font-body">
              You won't receive further marketing or notification emails from us.
            </p>
          </>
        )}
        {(state === "invalid" || state === "error") && (
          <>
            <XCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
            <h1 className="text-3xl font-display tracking-widest mb-3">LINK INVALID</h1>
            <p className="text-muted-foreground font-body">
              This unsubscribe link is invalid or expired. Reply to any of our emails and
              we'll remove you manually.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default Unsubscribe;